import { useEffect, useState } from "react";
import { Save, Images } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "../../lib/api";
import ImageField from "./ImageField";
import FocalPointPicker from "./FocalPointPicker";

const inputCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-md px-3 py-2 text-sm outline-none transition-colors";

const SECTIONS = [
  {
    key: "about",
    label: "About / Our Story",
    fields: [
      { name: "eyebrow", label: "Eyebrow (small text above title)" },
      { name: "title", label: "Title" },
      { name: "body", label: "Body Text", type: "textarea", rows: 4 },
      { name: "quote", label: "Quote", type: "textarea", rows: 2 },
      { name: "image1", label: "About Photo 1 (large image)", type: "image" },
      { name: "image1_alt", label: "About Photo 1 Alt Text" },
      { name: "image1_title", label: "About Photo 1 SEO Title" },
      { name: "image1_description", label: "About Photo 1 SEO Description", type: "textarea", rows: 2 },
      { name: "image1_caption", label: "About Photo 1 Caption (bottom right on click)" },
      { name: "image1_focal", label: "About Photo 1 Focal Point", type: "focal", source: "image1" },
      { name: "image2", label: "About Photo 2 (overlapping image)", type: "image" },
      { name: "image2_alt", label: "About Photo 2 Alt Text" },
      { name: "image2_title", label: "About Photo 2 SEO Title" },
      { name: "image2_description", label: "About Photo 2 SEO Description", type: "textarea", rows: 2 },
      { name: "image2_caption", label: "About Photo 2 Caption (bottom right on click)" },
      { name: "image2_focal", label: "About Photo 2 Focal Point", type: "focal", source: "image2" },
      { name: "stat1_value", label: "Stat 1 Value" },
      { name: "stat1_label", label: "Stat 1 Label" },
      { name: "stat2_value", label: "Stat 2 Value" },
      { name: "stat2_label", label: "Stat 2 Label" },
    ],
  },
  { key: "villas", label: "Stay / Villas Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" }, { name: "intro", label: "Intro Text", type: "textarea", rows: 3 },
  ]},
  { key: "dining", label: "Dining Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" }, { name: "intro", label: "Intro Text", type: "textarea", rows: 3 },
  ]},
  { key: "exp", label: "Experiences Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" }, { name: "intro", label: "Intro Text", type: "textarea", rows: 3 },
  ]},
  { key: "offers", label: "Offers Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" }, { name: "intro", label: "Intro Text", type: "textarea", rows: 3 },
  ]},
  { key: "gallery", label: "Gallery Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" },
  ]},
  { key: "reviews", label: "Guest Reviews Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" },
  ]},
  { key: "blog", label: "Blog / Journal Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" },
  ]},
  { key: "contact", label: "Contact Section", fields: [
    { name: "eyebrow", label: "Eyebrow" }, { name: "title", label: "Title" }, { name: "intro", label: "Intro Text", type: "textarea", rows: 3 },
  ]},
  { key: "footer", label: "Footer", fields: [
    { name: "tagline", label: "Footer Tagline", type: "textarea", rows: 2 },
  ]},
];

const MEDIA_GROUPS = [
  { key: "hero", label: "Hero Carousel", resource: "hero-slides", titleField: "title" },
  { key: "villas", label: "Stay / Villas", resource: "villas", titleField: "name" },
  { key: "dining", label: "Dining", resource: "dining", titleField: "title" },
  { key: "experiences", label: "Experiences", resource: "experiences", titleField: "title" },
  { key: "offers", label: "Offers", resource: "offers", titleField: "title" },
  { key: "gallery", label: "Gallery", resource: "gallery", titleField: "caption" },
  { key: "blog", label: "Blog / Journal Covers", resource: "posts", titleField: "title" },
];

export default function SectionsTab() {
  const [forms, setForms] = useState(null);
  const [mediaForms, setMediaForms] = useState(null);
  const [saving, setSaving] = useState(null);
  const [mediaSaving, setMediaSaving] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/content"),
      Promise.all(MEDIA_GROUPS.map((group) => api.get(`/admin/${group.resource}`))),
    ])
      .then(([contentResponse, mediaResponses]) => {
        const f = {};
        contentResponse.data.forEach((d) => (f[d.key] = d));
        const media = {};
        MEDIA_GROUPS.forEach((group, index) => {
          media[group.key] = mediaResponses[index].data;
        });
        setForms(f);
        setMediaForms(media);
      })
      .catch(() => toast.error("Failed to load page content"));
  }, []);

  if (!forms || !mediaForms) return <p className="text-sm text-[#A0988E]">Loading...</p>;

  const save = async (key) => {
    setSaving(key);
    try {
      await api.put(`/admin/content/${key}`, forms[key] || {});
      toast.success("Section saved - live on the website");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const setMediaValue = (groupKey, itemId, value, field = "image_url") => {
    setMediaForms({
      ...mediaForms,
      [groupKey]: mediaForms[groupKey].map((item) => (item.id === itemId ? { ...item, [field]: value } : item)),
    });
  };

  const saveMedia = async (group, item) => {
    const saveKey = `${group.key}-${item.id}`;
    setMediaSaving(saveKey);
    try {
      await api.put(`/admin/${group.resource}/${item.id}`, {
        image_url: item.image_url || "",
        image_alt: item.image_alt || "",
        image_title: item.image_title || "",
        image_description: item.image_description || "",
        image_caption: item.image_caption || item.caption || "",
        image_focal_point: item.image_focal_point || "50% 0%",
      });
      toast.success("Photo updated - live on the website");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMediaSaving(null);
    }
  };

  return (
    <div data-testid="sections-tab" className="max-w-5xl">
      <h2 className="font-serif text-2xl text-[#2C2825] mb-1">Page Sections</h2>
      <p className="text-sm text-[#A0988E] mb-8">
        Edit section wording and replace any photo that appears on the live website. Open a photo group below, upload or
        paste a new image URL, then save that photo.
      </p>
      <div data-testid="section-media-manager" className="mb-8 bg-white border border-[#E5DED4] rounded-md overflow-hidden">
        <div className="flex items-start gap-3 px-6 py-5 border-b border-[#E5DED4] bg-[#FBF9F5]">
          <span className="w-10 h-10 rounded-full bg-[#9A7B66]/10 text-[#9A7B66] flex items-center justify-center shrink-0">
            <Images className="w-5 h-5" />
          </span>
          <div>
            <h3 className="font-serif text-lg text-[#2C2825]">Live Section Photos</h3>
            <p className="text-xs text-[#A0988E] font-sans mt-1">
              Replace photos per section. Hero is limited to 10 photos and Gallery is limited to 50 photos.
            </p>
          </div>
        </div>
        <div className="divide-y divide-[#E5DED4]">
          {MEDIA_GROUPS.map((group) => (
            <details key={group.key} data-testid={`section-media-group-${group.key}`} className="group" open={group.key === "hero"}>
              <summary
                data-testid={`section-media-group-toggle-${group.key}`}
                className="cursor-pointer list-none px-6 py-4 flex items-center justify-between gap-4 hover:bg-[#FBF9F5] transition-colors"
              >
                <span className="font-sans font-semibold text-sm text-[#2C2825]">{group.label}</span>
                <span className="text-[11px] uppercase tracking-[0.14em] font-sans font-semibold text-[#9A7B66]">
                  {mediaForms[group.key]?.length || 0} photos
                </span>
              </summary>
              <div className="grid sm:grid-cols-2 gap-4 px-6 pb-6 pt-2">
                {(mediaForms[group.key] || []).map((item, itemIndex) => (
                  <div
                    key={item.id || itemIndex}
                    data-testid={`section-media-item-${group.key}-${itemIndex}`}
                    className="rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-4"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      {item.image_url ? (
                        <img
                          src={mediaUrl(item.image_url)}
                          alt={item[group.titleField] || group.label}
                          className="w-20 h-16 rounded-md object-cover bg-white border border-[#E5DED4] shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-16 rounded-md bg-[#E9E1D7] flex items-center justify-center text-[#A0988E] shrink-0">
                          <Images className="w-5 h-5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-sans font-semibold text-sm text-[#2C2825] truncate">
                          {item[group.titleField] || `${group.label} photo`}
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#A0988E] font-sans mt-1">
                          Photo {itemIndex + 1}
                        </p>
                      </div>
                    </div>
                    <ImageField
                      testId={`section-media-input-${group.key}-${itemIndex}`}
                      value={item.image_url || ""}
                      onChange={(value) => setMediaValue(group.key, item.id, value)}
                      accept="image/*"
                    />
                    <FocalPointPicker
                      testId={`section-media-focal-${group.key}-${itemIndex}`}
                      imageUrl={item.image_url || ""}
                      value={item.image_focal_point || "50% 0%"}
                      onChange={(value) => setMediaValue(group.key, item.id, value, "image_focal_point")}
                    />
                    <div className="mt-3 grid gap-2">
                      <input
                        data-testid={`section-media-alt-${group.key}-${itemIndex}`}
                        value={item.image_alt || ""}
                        onChange={(e) => setMediaValue(group.key, item.id, e.target.value, "image_alt")}
                        placeholder="Image alt text (optional)"
                        className={`${inputCls} !py-2 text-xs`}
                      />
                      <input
                        data-testid={`section-media-title-${group.key}-${itemIndex}`}
                        value={item.image_title || ""}
                        onChange={(e) => setMediaValue(group.key, item.id, e.target.value, "image_title")}
                        placeholder="Image SEO title (optional)"
                        className={`${inputCls} !py-2 text-xs`}
                      />
                      <input
                        data-testid={`section-media-caption-${group.key}-${itemIndex}`}
                        value={item.image_caption || item.caption || ""}
                        onChange={(e) => setMediaValue(group.key, item.id, e.target.value, "image_caption")}
                        placeholder="Photo caption - bottom right on click (optional)"
                        className={`${inputCls} !py-2 text-xs`}
                      />
                      <textarea
                        data-testid={`section-media-description-${group.key}-${itemIndex}`}
                        rows={2}
                        value={item.image_description || ""}
                        onChange={(e) => setMediaValue(group.key, item.id, e.target.value, "image_description")}
                        placeholder="Image SEO description (optional)"
                        className={`${inputCls} !py-2 text-xs resize-y`}
                      />
                    </div>
                    <button
                      data-testid={`section-media-save-${group.key}-${itemIndex}`}
                      onClick={() => saveMedia(group, item)}
                      disabled={mediaSaving === `${group.key}-${item.id}`}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#9A7B66] hover:bg-[#826552] disabled:opacity-50 text-white text-[11px] font-semibold uppercase tracking-wider transition-colors"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {mediaSaving === `${group.key}-${item.id}` ? "Saving..." : "Save Photo"}
                    </button>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {SECTIONS.map((sec) => (
          <div key={sec.key} data-testid={`section-form-${sec.key}`} className="bg-white border border-[#E5DED4] rounded-md p-6">
            <h3 className="font-serif text-lg text-[#2C2825] mb-4 pb-3 border-b border-[#E5DED4]">{sec.label}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.type === "image" || f.type === "focal" ? "sm:col-span-2" : ""}>
                  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">
                    {f.label}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      data-testid={`section-${sec.key}-${f.name}`}
                      rows={f.rows || 3}
                      value={forms[sec.key]?.[f.name] || ""}
                      onChange={(e) => setForms({ ...forms, [sec.key]: { ...forms[sec.key], [f.name]: e.target.value } })}
                      className={`${inputCls} resize-y`}
                    />
                  ) : f.type === "image" ? (
                    <ImageField
                      testId={`section-${sec.key}-${f.name}`}
                      value={forms[sec.key]?.[f.name] || ""}
                      onChange={(value) => setForms({ ...forms, [sec.key]: { ...forms[sec.key], [f.name]: value } })}
                      accept="image/*"
                    />
                  ) : f.type === "focal" ? (
                    <FocalPointPicker
                      testId={`section-${sec.key}-${f.name}`}
                      imageUrl={forms[sec.key]?.[f.source] || ""}
                      value={forms[sec.key]?.[f.name] || "50% 0%"}
                      onChange={(value) => setForms({ ...forms, [sec.key]: { ...forms[sec.key], [f.name]: value } })}
                    />
                  ) : (
                    <input
                      data-testid={`section-${sec.key}-${f.name}`}
                      value={forms[sec.key]?.[f.name] || ""}
                      onChange={(e) => setForms({ ...forms, [sec.key]: { ...forms[sec.key], [f.name]: e.target.value } })}
                      className={inputCls}
                    />
                  )}
                </div>
              ))}
            </div>
            <button
              data-testid={`section-save-${sec.key}`}
              onClick={() => save(sec.key)}
              disabled={saving === sec.key}
              className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-[#9A7B66] hover:bg-[#826552] disabled:opacity-50 text-white text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {saving === sec.key ? "Saving..." : "Save Section"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
